import {
  InternalService,
  type AiJobHandle,
  type AiJobStatusResponse,
  type HeuristicEvaluationRequest,
  type HeuristicEvaluationResponse,
  type HeuristicImprovementRequest,
  type HeuristicImprovementResponse,
} from "@/generated";

const POLL_INTERVAL_MS = 1_000;
const POLL_TIMEOUT_MS = 120_000;

const FAILED_STATUSES = new Set(["failed", "cancelled", "aborted"]);

export class PromptJobFailedError extends Error {
  readonly identifier: string | null;

  constructor(message: string, identifier: string | null = null) {
    super(message || "Job failed");
    this.name = "PromptJobFailedError";
    this.identifier = identifier;
  }
}

export class PromptJobTimeoutError extends Error {
  constructor() {
    super("Timed out waiting for job result");
    this.name = "PromptJobTimeoutError";
  }
}

// Covers both the job-failure identifier and a synchronous axios error
// from the job-creation POST.
export function isAllModelsDisabledError(error: unknown): boolean {
  if (error instanceof PromptJobFailedError) {
    return error.identifier === "all_models_disabled";
  }
  const axiosErr = error as {
    response?: { data?: { identifier?: string } };
  };
  return axiosErr?.response?.data?.identifier === "all_models_disabled";
}

function abortError(): Error {
  return new DOMException("The operation was aborted.", "AbortError");
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

interface PromptJobOptions<TBody> {
  path: { group_id: string };
  body: TBody;
  signal?: AbortSignal;
}

// Job results stay readable until their backend TTL (~5 min) expires, so
// duplicate polls are safe; a succeeded status with a null result means the
// result already expired.
async function pollPromptJob<TResult>(
  groupId: string,
  handle: AiJobHandle,
  signal?: AbortSignal
): Promise<{ data: TResult }> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS, signal);

    const { data: job }: { data: AiJobStatusResponse } =
      await InternalService.getPromptJobStatus({
        path: { group_id: groupId, job_id: handle.job_id },
        signal,
      });

    if (job.status === "succeeded") {
      if (job.result != null) {
        return { data: job.result as TResult };
      }
      // Result already popped by another consumer or expired — terminal, so
      // don't spin until the deadline pretending it might still arrive.
      throw new PromptJobFailedError("Job result is no longer available");
    }
    if (FAILED_STATUSES.has(job.status)) {
      throw new PromptJobFailedError(job.error ?? "", job.identifier ?? null);
    }
  }

  throw new PromptJobTimeoutError();
}

export async function runHeuristicEvaluationJob({
  path,
  body,
  signal,
}: PromptJobOptions<HeuristicEvaluationRequest>): Promise<{
  data: HeuristicEvaluationResponse;
}> {
  const { data: handle } = await InternalService.evaluatePromptHeuristicsJob({
    path,
    body,
    signal,
  });
  return pollPromptJob(path.group_id, handle, signal);
}

export async function runHeuristicImprovementJob({
  path,
  body,
  signal,
}: PromptJobOptions<HeuristicImprovementRequest>): Promise<{
  data: HeuristicImprovementResponse;
}> {
  const { data: handle } = await InternalService.improvePromptWithHeuristicsJob(
    {
      path,
      body,
      signal,
    }
  );
  return pollPromptJob(path.group_id, handle, signal);
}
